// WO 8.2 — Client-side image downscale.
//
// Phone cameras routinely emit 12-MP JPEGs (4-8 MB). Re-encoding them
// in the browser to a max-long-edge of MAX_EDGE_PX at JPEG quality 0.85
// drops a typical capture to under 1 MB without losing OCR fidelity
// (Claude vision works fine at 1600-2000 px). This is the main
// mobile-failure fix — without it, a slow uplink + a 6 MB phone JPEG
// is the most common reason an upload stalls.
//
// PDFs pass through untouched (no client-side rasterisation; the
// vision model handles PDFs natively).
//
// HEIC/HEIF: browsers cannot decode HEIC on canvas, so we pass them
// through. The server caps at 10 MB and Claude vision accepts HEIC.

const MAX_EDGE_PX = 2000;        // long-edge target
const JPEG_QUALITY = 0.85;
const DOWNSCALE_TRIGGER_BYTES = 1.5 * 1024 * 1024;  // ≥ 1.5 MB → downscale
const HEIC_MIMES = new Set(["image/heic", "image/heif"]);

function isImage(mime) {
  return typeof mime === "string" && mime.startsWith("image/");
}

function loadBitmap(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function maybeDownscaleImage(file) {
  // Pass-through fast paths
  if (!file) return file;
  if (!isImage(file.type)) return file;            // pdfs pass through
  if (HEIC_MIMES.has(file.type)) return file;      // browser can't decode HEIC
  if (file.size < DOWNSCALE_TRIGGER_BYTES) return file;

  let img;
  try {
    img = await loadBitmap(file);
  } catch {
    return file;  // decode failed; let the server reject if it's bad
  }
  const { width: w0, height: h0 } = img;
  const long = Math.max(w0, h0);
  if (long <= MAX_EDGE_PX) return file;

  const scale = MAX_EDGE_PX / long;
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
  if (!blob) return file;  // canvas encode failed; fall back to original

  // Preserve the original base name; force .jpg extension since we
  // re-encoded as JPEG.
  const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`,
    { type: "image/jpeg", lastModified: Date.now() });
}

export async function maybeDownscaleAll(files) {
  return Promise.all((files || []).map(maybeDownscaleImage));
}
